import React, { useEffect, useMemo, useState } from "react";
import { API, Auth, graphqlOperation } from "aws-amplify";
import { Link } from "react-router-dom";
import PlayerProfileModal from "../components/PlayerProfileModal";
import {
  listLeagues,
  listOrganizations,
  listPlayerParents,
  listPitchLogs,
  listPlayers,
  listRestProfiles,
  listRestRules,
  listRosterMemberships,
  listTeamPlayers,
  listTeams,
  listUserAssignments,
  listUsers as listAppUsers
} from "../graphql/queries";
import { evaluatePitcherAvailability, PlayerAvailability } from "../lib/rulesEngine";
import { buildCoachTeamIds, buildPlayerTeamIds, getTeamCoachUsernames, type RosterMembershipLike, type UserAssignmentLike } from "../lib/relationshipScope";
import { isProgramAdmin } from "../lib/roles";
import type { ActiveRoleScope } from "../lib/roleContext";

interface DashboardProps {
  userRoles: string[];
  activeRoleScope?: ActiveRoleScope | null;
}

interface CardStat {
  id: string;
  label: string;
  value: number;
}

interface ParentChildCard {
  playerId: string;
  playerName: string;
  teamName: string;
  latestOuting: {
    date: string;
    teamName: string;
    pitches: number;
    outsRecorded?: number | null;
  } | null;
  violationCountLast2Weeks: number;
}

interface TeamPlayerLink {
  id: string;
  teamID: string;
  playerID: string;
}

interface PlayerParentLink {
  id: string;
  playerId: string;
  parentEmail: string;
}

interface RosterMembership extends RosterMembershipLike {}

interface UserAssignment extends UserAssignmentLike {}

interface AppUserLite {
  id: string;
  givenName?: string | null;
  familyName?: string | null;
  email?: string | null;
}

interface ViolationRankRow {
  id: string;
  label: string;
  count: number;
}

interface DashboardViolationRow {
  id: string;
  playerId: string;
  playerName: string;
  teamId?: string | null;
  teamName: string;
  date: string;
  violations: PlayerAvailability["violations"];
}

const platformModules = [
  {
    title: "Roster Ops",
    body: "Teams, players, guardians, coach assignments, and player history.",
    href: "/admin/players",
    roles: ["all"]
  },
  {
    title: "Evaluation Pipeline",
    body: "Mobile tryout scoring with draft-ready aggregate rankings.",
    href: "/admin/evaluations",
    roles: ["staff"]
  },
  {
    title: "Draft Room",
    body: "Balance teams, tier rosters, and finalize players into teams.",
    href: "/admin/drafts",
    roles: ["admin"]
  },
  {
    title: "Arm Health",
    body: "Pitch logs, rest windows, rules, and violation detection.",
    href: "/admin/pitch-logs",
    roles: ["all"]
  },
  {
    title: "Game Day",
    body: "Lineup planning, defensive rotations, and share-board views.",
    href: "/admin/teams",
    roles: ["staff"]
  },
  {
    title: "Fields & Schedule",
    body: "CSV/iCal imports, field availability, and conflict checks.",
    href: "/admin/schedule",
    roles: ["staff"]
  }
];

const countItems = (data: any, key: string): number => data?.[key]?.items?.filter(Boolean)?.length ?? 0;
const EXTERNAL_TEAM_PREFIX = "external:";
const getTeamDisplayName = (rawTeamId: string, teamById: Record<string, any>): string => {
  if (!rawTeamId) {
    return "Unknown team";
  }
  if (rawTeamId.startsWith(EXTERNAL_TEAM_PREFIX)) {
    const externalName = rawTeamId.slice(EXTERNAL_TEAM_PREFIX.length).trim();
    return externalName ? `${externalName} (External)` : "External team (External)";
  }
  return teamById[rawTeamId]?.name ?? "Unknown team";
};
const severityColor = (daysRemaining: number): string => {
  if (daysRemaining >= 4) {
    return "#b91c1c";
  }
  if (daysRemaining >= 2) {
    return "#c2410c";
  }
  return "#ca8a04";
};

const formatDisplayDate = (date: string | null | undefined): string => {
  if (!date) {
    return "Unknown";
  }
  const parsed = new Date(`${date}T00:00:00`);
  if (Number.isNaN(parsed.getTime())) {
    return date;
  }
  return parsed.toLocaleDateString(undefined, {
    month: "numeric",
    day: "numeric",
    year: "numeric"
  });
};

const groupViolationsByTeamAndDate = (entries: PlayerAvailability[]): DashboardViolationRow[] => {
  const grouped = new Map<string, DashboardViolationRow>();
  for (const entry of entries) {
    for (const violation of entry.violations) {
      const teamName = violation.teamName ?? entry.teamName;
      const key = `${entry.playerId}|${violation.teamId ?? teamName}|${violation.occurredOn}`;
      const existing = grouped.get(key);
      if (existing) {
        existing.violations.push(violation);
        continue;
      }
      grouped.set(key, {
        id: key,
        playerId: entry.playerId,
        playerName: entry.playerName,
        teamId: violation.teamId,
        teamName,
        date: violation.occurredOn,
        violations: [violation]
      });
    }
  }

  return [...grouped.values()].map((row) => ({
    ...row,
    violations: row.violations.filter(
      (violation, index, arr) =>
        arr.findIndex(
          (candidate) =>
            candidate.type === violation.type &&
            candidate.ruleName === violation.ruleName &&
            candidate.source === violation.source &&
            candidate.teamId === violation.teamId
        ) === index
    )
  }));
};

const AdminDashboard: React.FC<DashboardProps> = ({ userRoles }) => {
  const [stats, setStats] = useState<CardStat[]>([]);
  const [statOrder, setStatOrder] = useState<string[]>([]);
  const [draggingStatId, setDraggingStatId] = useState<string | null>(null);
  const [parentCardOrder, setParentCardOrder] = useState<string[]>([]);
  const [draggingParentCardId, setDraggingParentCardId] = useState<string | null>(null);
  const [panelOrder, setPanelOrder] = useState<string[]>([]);
  const [draggingPanelId, setDraggingPanelId] = useState<string | null>(null);
  const [availability, setAvailability] = useState<PlayerAvailability[]>([]);
  const [outsideUsage, setOutsideUsage] = useState<Array<{ playerId: string; playerName: string; teamName: string; date: string }>>([]);
  const [parentChildCards, setParentChildCards] = useState<ParentChildCard[]>([]);
  const [topTeamsByViolations, setTopTeamsByViolations] = useState<ViolationRankRow[]>([]);
  const [topCoachesByViolations, setTopCoachesByViolations] = useState<ViolationRankRow[]>([]);
  const [errorMessage, setErrorMessage] = useState("");
  const [activePlayerModalId, setActivePlayerModalId] = useState<string | null>(null);
  const isPrivileged = userRoles.includes("Commish") || userRoles.includes("SuperAdmin");
  const isSuperOrCommish = userRoles.includes("Commish") || userRoles.includes("SuperAdmin");
  const hasCoachRole = userRoles.includes("Coach");
  const hasParentRole = userRoles.includes("Parent") || userRoles.includes("Parents");
  const canUseAdminModules = isProgramAdmin(userRoles);

  useEffect(() => {
    const loadStats = async () => {
      try {
        setErrorMessage("");
        const authUser = await Auth.currentAuthenticatedUser();
        const username = authUser?.username ?? "";
        const userEmail = (authUser?.attributes?.email ?? "").toLowerCase();
        const [orgRes, leagueRes, teamRes, playerRes, logsRes, rulesRes, profilesRes, teamPlayersRes, rosterMembershipsRes, userAssignmentsRes, playerParentsRes] = await Promise.all([
          API.graphql(graphqlOperation(listOrganizations, { limit: 500 })),
          API.graphql(graphqlOperation(listLeagues, { limit: 500 })),
          API.graphql(graphqlOperation(listTeams, { limit: 500 })),
          API.graphql(graphqlOperation(listPlayers, { limit: 1000 })),
          API.graphql(graphqlOperation(listPitchLogs, { limit: 3000 })),
          API.graphql(graphqlOperation(listRestRules, { limit: 500 })),
          API.graphql(graphqlOperation(listRestProfiles, { limit: 2000 })),
          API.graphql(graphqlOperation(listTeamPlayers, { limit: 4000 })),
          API.graphql(graphqlOperation(listRosterMemberships, { limit: 5000 })),
          API.graphql(graphqlOperation(listUserAssignments, { limit: 5000 })),
          API.graphql(graphqlOperation(listPlayerParents, { limit: 6000 }))
        ]);
        let appUsers: AppUserLite[] = [];
        try {
          const usersRes = await API.graphql(graphqlOperation(listAppUsers, { limit: 3000 }));
          appUsers = ((usersRes as any)?.data?.listUsers?.items ?? []).filter(Boolean);
        } catch {}

        const organizations = ((orgRes as any)?.data?.listOrganizations?.items ?? []).filter(Boolean);
        const leagues = ((leagueRes as any)?.data?.listLeagues?.items ?? []).filter(Boolean);
        const teams = ((teamRes as any)?.data?.listTeams?.items ?? []).filter(Boolean);
        const players = ((playerRes as any)?.data?.listPlayers?.items ?? []).filter(Boolean);
        const logs = ((logsRes as any)?.data?.listPitchLogs?.items ?? []).filter(Boolean);
        const rules = ((rulesRes as any)?.data?.listRestRules?.items ?? []).filter(Boolean);
        const profiles = ((profilesRes as any)?.data?.listRestProfiles?.items ?? []).filter(Boolean);
        const teamPlayers: TeamPlayerLink[] = ((teamPlayersRes as any)?.data?.listTeamPlayers?.items ?? []).filter(Boolean);
        const rosterMemberships: RosterMembership[] = ((rosterMembershipsRes as any)?.data?.listRosterMemberships?.items ?? []).filter(Boolean);
        const userAssignments: UserAssignment[] = ((userAssignmentsRes as any)?.data?.listUserAssignments?.items ?? []).filter(Boolean);
        const playerParents: PlayerParentLink[] = ((playerParentsRes as any)?.data?.listPlayerParents?.items ?? []).filter(Boolean);

        const teamById: Record<string, any> = {};
        for (const team of teams) {
          teamById[team.id] = team;
        }
        const coachDisplayByUsername: Record<string, string> = {};
        for (const user of appUsers) {
          const fullName = `${user.givenName ?? ""} ${user.familyName ?? ""}`.trim();
          coachDisplayByUsername[user.id] = fullName || user.email || user.id;
        }

        const playerMemberships = buildPlayerTeamIds({ players, teamPlayers, rosterMemberships });

        const coachTeamIds = hasCoachRole && !isPrivileged
          ? buildCoachTeamIds({ username, teams, userAssignments })
          : new Set<string>();

        const parentPlayerIds = new Set<string>(
          hasParentRole && !isPrivileged
            ? playerParents
                .filter((link) => (link.parentEmail ?? "").toLowerCase() === userEmail)
                .map((link) => link.playerId)
            : []
        );

        const visiblePlayerIds = new Set<string>();
        const coachedPlayerIds = new Set<string>();
        if (isPrivileged || (!hasCoachRole && !hasParentRole)) {
          for (const player of players) {
            visiblePlayerIds.add(player.id);
          }
        } else {
          if (hasCoachRole) {
            for (const player of players) {
              if ([...(playerMemberships[player.id] ?? new Set<string>())].some((teamId) => coachTeamIds.has(teamId))) {
                visiblePlayerIds.add(player.id);
                coachedPlayerIds.add(player.id);
              }
            }
          }
          if (hasParentRole) {
            for (const playerId of parentPlayerIds) {
              visiblePlayerIds.add(playerId);
            }
          }
        }

        const scopedPlayers = players.filter((player: any) => visiblePlayerIds.has(player.id));
        const scopedLogs = logs.filter((log: any) => visiblePlayerIds.has(log.playerId));
        const scopedTeams = isPrivileged || (!hasCoachRole && !hasParentRole)
          ? teams
          : teams.filter((team: any) =>
              (hasCoachRole && coachTeamIds.has(team.id)) ||
              scopedPlayers.some((player: any) => [...(playerMemberships[player.id] ?? new Set<string>())].includes(team.id))
            );

        const computedAvailability = evaluatePitcherAvailability({
          players: scopedPlayers,
          teams,
          leagues,
          organizations,
          rules,
          profiles,
          logs: scopedLogs,
          teamPlayers
        });

        const cutoff = new Date();
        cutoff.setDate(cutoff.getDate() - 14);
        const cutoffIso = cutoff.toISOString().slice(0, 10);
        const totalViolations = computedAvailability.filter((item) =>
          item.violations.some((violation) => violation.occurredOn >= cutoffIso)
        ).length;
        const violationRowsLast2Weeks = computedAvailability.map((entry) => ({
          ...entry,
          violations: entry.violations.filter((violation) => violation.occurredOn >= cutoffIso)
        })).filter((entry) => entry.violations.length > 0);

        const availabilityByPlayerId = new Map<string, PlayerAvailability>(
          computedAvailability.map((entry) => [entry.playerId, entry])
        );
        const latestLogByPlayerId: Record<string, any> = {};
        for (const log of scopedLogs) {
          const existing = latestLogByPlayerId[log.playerId];
          const candidateDate = log.date ?? "";
          const existingDate = existing?.date ?? "";
          const candidateUpdated = log.updatedAt ?? "";
          const existingUpdated = existing?.updatedAt ?? "";
          if (
            !existing ||
            candidateDate > existingDate ||
            (candidateDate === existingDate && candidateUpdated > existingUpdated)
          ) {
            latestLogByPlayerId[log.playerId] = log;
          }
        }

        const latestOutsideUseByPlayer: Record<string, { playerId: string; playerName: string; teamName: string; date: string }> = {};
        if (hasCoachRole && !isPrivileged) {
          for (const log of scopedLogs) {
            if (!coachedPlayerIds.has(log.playerId)) {
              continue;
            }
            if (!coachTeamIds.has(log.teamId)) {
              const existing = latestOutsideUseByPlayer[log.playerId];
              if (!existing || log.date > existing.date) {
                const playerName = scopedPlayers.find((player: any) => player.id === log.playerId)?.name ?? "Unknown player";
                latestOutsideUseByPlayer[log.playerId] = {
                  playerId: log.playerId,
                  playerName,
                  teamName: getTeamDisplayName(log.teamId, teamById),
                  date: log.date
                };
              }
            }
          }
        }

        setOutsideUsage(Object.values(latestOutsideUseByPlayer).sort((a, b) => b.date.localeCompare(a.date)));

        const violationRowsByTeam = groupViolationsByTeamAndDate(violationRowsLast2Weeks);
        const teamViolationCounts = new Map<string, number>();
        const coachViolationCounts = new Map<string, number>();
        for (const row of violationRowsByTeam) {
          teamViolationCounts.set(row.teamName, (teamViolationCounts.get(row.teamName) ?? 0) + row.violations.length);
          const teamForViolation = row.teamId ? teamById[row.teamId] : undefined;
          const coachesForTeam: string[] = teamForViolation ? getTeamCoachUsernames({ team: teamForViolation, userAssignments }) : [];
          for (const coachUsername of coachesForTeam) {
            coachViolationCounts.set(coachUsername, (coachViolationCounts.get(coachUsername) ?? 0) + row.violations.length);
          }
        }

        const topTeams = [...teamViolationCounts.entries()]
          .map(([label, count]) => ({ id: label, label, count }))
          .sort((a, b) => (b.count - a.count) || a.label.localeCompare(b.label))
          .slice(0, 8);
        const topCoaches = [...coachViolationCounts.entries()]
          .map(([coachUsername, count]) => ({ id: coachUsername, label: coachDisplayByUsername[coachUsername] ?? coachUsername, count }))
          .sort((a, b) => (b.count - a.count) || a.label.localeCompare(b.label))
          .slice(0, 8);

        setTopTeamsByViolations(topTeams);
        setTopCoachesByViolations(topCoaches);

        setStats([
          ...((hasParentRole && !hasCoachRole && !isPrivileged) ? [] : [
            { id: "organizations", label: "Organizations", value: countItems((orgRes as any).data, "listOrganizations") },
            { id: "leagues", label: "Leagues", value: countItems((leagueRes as any).data, "listLeagues") }
          ]),
          { id: "teams", label: "Teams", value: scopedTeams.length },
          { id: "players", label: "Players", value: scopedPlayers.length },
          { id: "violations", label: "Violations", value: totalViolations }
        ] as CardStat[]);

        setAvailability(computedAvailability);
        if (hasParentRole) {
          const parentCards = [...parentPlayerIds]
            .map((id) => scopedPlayers.find((player: any) => player.id === id))
            .filter(Boolean)
            .map((player: any) => {
              const latest = latestLogByPlayerId[player.id];
              const availabilityEntry = availabilityByPlayerId.get(player.id);
              const violationsLast2Weeks = (availabilityEntry?.violations ?? []).filter(
                (violation) => violation.occurredOn >= cutoffIso
              ).length;
              return {
                playerId: player.id,
                playerName: player.name,
                teamName: teamById[player.teamId]?.name ?? "Unknown team",
                latestOuting: latest
                  ? {
                      date: latest.date,
                      teamName: teamById[latest.teamId]?.name ?? "Unknown team",
                      pitches: latest.pitches,
                      outsRecorded: latest.outsRecorded
                    }
                  : null,
                violationCountLast2Weeks: violationsLast2Weeks
              } as ParentChildCard;
            })
            .sort((a, b) => a.playerName.localeCompare(b.playerName));
          setParentChildCards(parentCards);
        } else {
          setParentChildCards([]);
        }
      } catch (error) {
        console.error("Error loading dashboard stats:", error);
        setErrorMessage(error instanceof Error ? error.message : "Unknown error");
      }
    };

    loadStats();
  }, [hasCoachRole, hasParentRole, isPrivileged]);

  useEffect(() => {
    if (stats.length === 0) {
      return;
    }
    const savedRaw = window.localStorage.getItem("lf-dashboard-stat-order");
    const saved = savedRaw ? JSON.parse(savedRaw) : [];
    const validSaved = Array.isArray(saved) ? saved.filter((id) => stats.some((stat) => stat.id === id)) : [];
    const missing = stats.map((stat) => stat.id).filter((id) => !validSaved.includes(id));
    setStatOrder([...validSaved, ...missing]);
  }, [stats]);

  useEffect(() => {
    if (statOrder.length > 0) {
      window.localStorage.setItem("lf-dashboard-stat-order", JSON.stringify(statOrder));
    }
  }, [statOrder]);

  const orderedStats = useMemo(() => {
    if (statOrder.length === 0) {
      return stats;
    }
    const statById = new Map(stats.map((stat) => [stat.id, stat]));
    return statOrder.map((id) => statById.get(id)).filter((stat): stat is CardStat => Boolean(stat));
  }, [stats, statOrder]);

  useEffect(() => {
    if (parentChildCards.length === 0) {
      setParentCardOrder([]);
      return;
    }
    const savedRaw = window.localStorage.getItem("lf-dashboard-parent-card-order");
    const saved = savedRaw ? JSON.parse(savedRaw) : [];
    const validSaved = Array.isArray(saved) ? saved.filter((id) => parentChildCards.some((card) => card.playerId === id)) : [];
    const missing = parentChildCards.map((card) => card.playerId).filter((id) => !validSaved.includes(id));
    setParentCardOrder([...validSaved, ...missing]);
  }, [parentChildCards]);

  useEffect(() => {
    if (parentCardOrder.length > 0) {
      window.localStorage.setItem("lf-dashboard-parent-card-order", JSON.stringify(parentCardOrder));
    }
  }, [parentCardOrder]);

  const orderedParentCards = useMemo(() => {
    if (parentCardOrder.length === 0) {
      return parentChildCards;
    }
    const cardById = new Map(parentChildCards.map((card) => [card.playerId, card]));
    return parentCardOrder.map((id) => cardById.get(id)).filter((card): card is ParentChildCard => Boolean(card));
  }, [parentCardOrder, parentChildCards]);

  const visiblePanelIds = useMemo(() => {
    const ids: string[] = [];
    if (hasCoachRole && !isPrivileged) {
      ids.push("outsideUsage");
    }
    if (isSuperOrCommish) {
      ids.push("topTeams", "topCoaches");
    }
    ids.push("unavailablePitchers", "availablePitchers", "ruleViolations");
    return ids;
  }, [hasCoachRole, isPrivileged, isSuperOrCommish]);

  useEffect(() => {
    if (visiblePanelIds.length === 0) {
      setPanelOrder([]);
      return;
    }
    const savedRaw = window.localStorage.getItem("lf-dashboard-panel-order");
    const saved = savedRaw ? JSON.parse(savedRaw) : [];
    const validSaved = Array.isArray(saved) ? saved.filter((id) => visiblePanelIds.includes(id)) : [];
    const missing = visiblePanelIds.filter((id) => !validSaved.includes(id));
    setPanelOrder([...validSaved, ...missing]);
  }, [visiblePanelIds]);

  useEffect(() => {
    if (panelOrder.length > 0) {
      window.localStorage.setItem("lf-dashboard-panel-order", JSON.stringify(panelOrder));
    }
  }, [panelOrder]);

  const orderedPanelIds = useMemo(() => {
    if (panelOrder.length === 0) {
      return visiblePanelIds;
    }
    return panelOrder.filter((id) => visiblePanelIds.includes(id));
  }, [panelOrder, visiblePanelIds]);

  const reorderStats = (fromId: string, toId: string) => {
    if (fromId === toId) {
      return;
    }
    setStatOrder((prev) => {
      const next = [...prev];
      const fromIndex = next.indexOf(fromId);
      const toIndex = next.indexOf(toId);
      if (fromIndex === -1 || toIndex === -1) {
        return prev;
      }
      const [moved] = next.splice(fromIndex, 1);
      next.splice(toIndex, 0, moved);
      return next;
    });
  };

  const reorderParentCards = (fromId: string, toId: string) => {
    if (fromId === toId) {
      return;
    }
    setParentCardOrder((prev) => {
      const next = [...prev];
      const fromIndex = next.indexOf(fromId);
      const toIndex = next.indexOf(toId);
      if (fromIndex === -1 || toIndex === -1) {
        return prev;
      }
      const [moved] = next.splice(fromIndex, 1);
      next.splice(toIndex, 0, moved);
      return next;
    });
  };

  const reorderPanels = (fromId: string, toId: string) => {
    if (fromId === toId) {
      return;
    }
    setPanelOrder((prev) => {
      const next = [...prev];
      const fromIndex = next.indexOf(fromId);
      const toIndex = next.indexOf(toId);
      if (fromIndex === -1 || toIndex === -1) {
        return prev;
      }
      const [moved] = next.splice(fromIndex, 1);
      next.splice(toIndex, 0, moved);
      return next;
    });
  };

  const unavailablePitchers = useMemo(
    () => availability.filter((entry) => !entry.available).sort((a, b) => a.playerName.localeCompare(b.playerName)),
    [availability]
  );
  const availablePitchers = useMemo(
    () => availability.filter((entry) => entry.available).sort((a, b) => a.playerName.localeCompare(b.playerName)),
    [availability]
  );
  const violations = useMemo(
    () => {
      const cutoff = new Date();
      cutoff.setDate(cutoff.getDate() - 14);
      const cutoffIso = cutoff.toISOString().slice(0, 10);
      const lastTwoWeeks = availability
        .map((entry) => ({
          ...entry,
          violations: entry.violations.filter((violation) => violation.occurredOn >= cutoffIso)
        }))
        .filter((entry) => entry.violations.length > 0);
      return groupViolationsByTeamAndDate(lastTwoWeeks).sort((a, b) => {
        const playerCompare = a.playerName.localeCompare(b.playerName);
        if (playerCompare !== 0) {
          return playerCompare;
        }
        return b.date.localeCompare(a.date);
      });
    },
    [availability]
  );

  return (
    <>
    <div className="platform-page">
      <section className="platform-hero">
        <div>
          <div className="platform-eyebrow">Legends Field Command Center</div>
          <h1>Travel baseball ops, from tryouts to pitch limits.</h1>
          <p>Signed in with roles: {userRoles.join(", ") || "No role assigned"}. The interface is being rebuilt around the new multi-tenant Gen 2 platform model.</p>
        </div>
        <div className="platform-hero__actions">
          <Link to="/admin/evaluations" className="bg-blue-600 text-white px-4 py-2 rounded">Open Evaluations</Link>
          <Link to="/admin/schedule" className="bg-green-600 text-white px-4 py-2 rounded">Schedule</Link>
        </div>
      </section>
      {errorMessage && <p style={{ color: "#c1121f" }} className="mb-4">Failed to load dashboard: {errorMessage}</p>}

      <div className="platform-grid">
        {platformModules
          .filter((module) => {
            if (module.roles.includes("admin")) {
              return canUseAdminModules;
            }
            if (module.roles.includes("staff")) {
              return canUseAdminModules || hasCoachRole;
            }
            return true;
          })
          .map((module) => (
            <Link key={module.title} to={module.href} className="bubble-card p-4 platform-module-card">
              <div className="platform-module-card__kicker">Module</div>
              <h2>{module.title}</h2>
              <p>{module.body}</p>
            </Link>
          ))}
      </div>

      {hasParentRole && !isPrivileged && (
        <div className="mb-6">
          <h2 className="text-lg font-semibold mb-3">My Kids</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {parentChildCards.length === 0 && (
              <div className="bubble-card p-4 text-gray-600">No linked player records found for your email yet.</div>
            )}
            {orderedParentCards.map((card) => (
              <div
                key={card.playerId}
                className="bubble-card p-4 dashboard-stat-card"
                draggable
                onDragStart={() => setDraggingParentCardId(card.playerId)}
                onDragOver={(event) => event.preventDefault()}
                onDrop={() => {
                  if (draggingParentCardId) {
                    reorderParentCards(draggingParentCardId, card.playerId);
                  }
                  setDraggingParentCardId(null);
                }}
                onDragEnd={() => setDraggingParentCardId(null)}
                title="Drag to rearrange"
              >
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <div className="font-semibold text-lg">
                      <button className="table-sort" onClick={() => setActivePlayerModalId(card.playerId)}>
                        {card.playerName}
                      </button>
                    </div>
                    <div className="text-sm text-gray-600">{card.teamName}</div>
                  </div>
                  {card.violationCountLast2Weeks > 0 ? (
                    <span
                      className="bubble-pill"
                      style={{ background: "#be123c", color: "#fff", borderColor: "#be123c" }}
                    >
                      {card.violationCountLast2Weeks} violation{card.violationCountLast2Weeks === 1 ? "" : "s"}
                    </span>
                  ) : (
                    <span className="bubble-pill" style={{ background: "#ecfdf3", color: "#166534", borderColor: "#bbf7d0" }}>
                      No recent violations
                    </span>
                  )}
                </div>
                <div className="mt-3 text-sm">
                  {card.latestOuting ? (
                    <div className="space-y-1">
                      <div><span className="font-medium">Latest outing:</span> {card.latestOuting.date}</div>
                      <div><span className="font-medium">Team:</span> {card.latestOuting.teamName}</div>
                      <div>
                        <span className="font-medium">Line:</span> {card.latestOuting.pitches} pitches,{" "}
                        {typeof card.latestOuting.outsRecorded === "number" ? card.latestOuting.outsRecorded : 0} outs
                      </div>
                    </div>
                  ) : (
                    <div className="text-gray-600">No outings logged yet.</div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
        {orderedStats.map((stat) => (
          <div
            key={stat.id}
            className="border rounded-md p-4 bg-white shadow-sm dashboard-stat-card"
            draggable
            onDragStart={() => setDraggingStatId(stat.id)}
            onDragOver={(event) => event.preventDefault()}
            onDrop={() => {
              if (draggingStatId) {
                reorderStats(draggingStatId, stat.id);
              }
              setDraggingStatId(null);
            }}
            onDragEnd={() => setDraggingStatId(null)}
            title="Drag to rearrange"
          >
            <div className="text-sm text-gray-500">{stat.label}</div>
            <div className="text-2xl font-semibold">{stat.value}</div>
          </div>
        ))}
      </div>

      <div className="mb-6" style={{ display: "flex", flexWrap: "wrap", gap: 16, alignItems: "flex-start" }}>
        {orderedPanelIds.map((panelId) => (
          <div
            key={panelId}
            className="border rounded-md p-4 bg-white shadow-sm dashboard-stat-card"
            style={{ width: "min(640px, 100%)", flex: "1 1 420px", minWidth: 280, maxWidth: 1200, minHeight: 190, maxHeight: 280, overflow: "hidden", resize: "horizontal" }}
            draggable
            onDragStart={() => setDraggingPanelId(panelId)}
            onDragOver={(event) => event.preventDefault()}
            onDrop={() => {
              if (draggingPanelId) {
                reorderPanels(draggingPanelId, panelId);
              }
              setDraggingPanelId(null);
            }}
            onDragEnd={() => setDraggingPanelId(null)}
            title="Drag to rearrange"
          >
            {panelId === "outsideUsage" && (
              <>
                <h2 className="text-lg font-semibold mb-2">Players Used By Other Teams</h2>
                <div style={{ maxHeight: 190, overflowY: "auto", paddingRight: 4 }}>
                  <ul className="space-y-2" style={{ listStyle: "none", margin: 0, padding: 0 }}>
                    {outsideUsage.length === 0 && <li className="text-gray-600">No cross-team usage detected for your players.</li>}
                    {outsideUsage.map((item) => (
                      <li key={item.playerId} className="border rounded p-3" style={{ padding: "10px 14px" }}>
                        <div className="font-medium" style={{ marginBottom: 4 }}>
                          <button className="table-sort" onClick={() => setActivePlayerModalId(item.playerId)}>{item.playerName}</button>
                        </div>
                        <div className="text-sm text-gray-600">Used by {item.teamName} on {item.date}</div>
                      </li>
                    ))}
                  </ul>
                </div>
              </>
            )}

            {panelId === "topTeams" && (
              <>
                <h2 className="text-lg font-semibold mb-2">Top Teams by Violations</h2>
                <div style={{ maxHeight: 190, overflowY: "auto", paddingRight: 4 }}>
                  <ul className="space-y-2" style={{ listStyle: "none", margin: 0, padding: 0 }}>
                    {topTeamsByViolations.length === 0 && <li className="text-gray-600">No team violations in the last 2 weeks.</li>}
                    {topTeamsByViolations.map((item) => (
                      <li key={item.id} className="border rounded p-3" style={{ padding: "10px 14px" }}>
                        <div className="font-medium" style={{ marginBottom: 4 }}>{item.label}</div>
                        <div className="text-sm text-gray-600">{item.count} violation{item.count === 1 ? "" : "s"} (last 2 weeks)</div>
                      </li>
                    ))}
                  </ul>
                </div>
              </>
            )}

            {panelId === "topCoaches" && (
              <>
                <h2 className="text-lg font-semibold mb-2">Top Coaches by Violations</h2>
                <div style={{ maxHeight: 190, overflowY: "auto", paddingRight: 4 }}>
                  <ul className="space-y-2" style={{ listStyle: "none", margin: 0, padding: 0 }}>
                    {topCoachesByViolations.length === 0 && <li className="text-gray-600">No coach-attributed violations in the last 2 weeks.</li>}
                    {topCoachesByViolations.map((item) => (
                      <li key={item.id} className="border rounded p-3" style={{ padding: "10px 14px" }}>
                        <div className="font-medium" style={{ marginBottom: 4 }}>{item.label}</div>
                        <div className="text-sm text-gray-600">{item.count} violation{item.count === 1 ? "" : "s"} (last 2 weeks)</div>
                      </li>
                    ))}
                  </ul>
                </div>
              </>
            )}

            {panelId === "unavailablePitchers" && (
              <>
                <div className="flex justify-between items-center mb-2">
                  <h2 className="text-lg font-semibold">Unavailable Pitchers</h2>
                  <Link to="/admin/pitch-logs" className="text-sm">Log Pitching</Link>
                </div>
                <div style={{ maxHeight: 190, overflowY: "auto", paddingRight: 4 }}>
                  <ul className="space-y-2" style={{ listStyle: "none", margin: 0, padding: 0 }}>
                    {unavailablePitchers.length === 0 && <li className="text-gray-600">No unavailable pitchers right now.</li>}
                    {unavailablePitchers.map((entry) => (
                      <li
                        key={entry.playerId}
                        className="border rounded p-3"
                        style={{ borderLeft: `6px solid ${severityColor(entry.daysRemaining)}`, padding: "10px 14px" }}
                      >
                        <div className="font-medium" style={{ marginBottom: 4 }}>
                          <button className="table-sort" onClick={() => setActivePlayerModalId(entry.playerId)}>{entry.playerName}</button> ({entry.teamName})
                        </div>
                        <div className="mt-1 text-sm text-gray-600">
                          Next eligible: {formatDisplayDate(entry.availableOn)}
                        </div>
                        <div className="mt-2 text-xs" style={{ color: severityColor(entry.daysRemaining) }}>
                          {entry.daysRemaining} day{entry.daysRemaining === 1 ? "" : "s"} remaining
                        </div>
                        <div
                          style={{
                            marginTop: 6,
                            width: "100%",
                            height: 8,
                            background: "#e5e7eb",
                            borderRadius: 999
                          }}
                        >
                          <div
                            style={{
                              width: `${entry.restProgressPct}%`,
                              height: "100%",
                              background: severityColor(entry.daysRemaining),
                              borderRadius: 999,
                              transition: "width 220ms ease"
                            }}
                          />
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              </>
            )}

            {panelId === "availablePitchers" && (
              <>
                <div className="flex justify-between items-center mb-2">
                  <h2 className="text-lg font-semibold">Available Pitchers</h2>
                  <Link to="/admin/players" className="text-sm">Manage Players</Link>
                </div>
                <div style={{ maxHeight: 190, overflowY: "auto", paddingRight: 4 }}>
                  <ul className="space-y-2" style={{ listStyle: "none", margin: 0, padding: 0 }}>
                    {availablePitchers.length === 0 && <li className="text-gray-600">No available pitchers found.</li>}
                    {availablePitchers.map((entry) => (
                      <li key={entry.playerId} className="border rounded p-3" style={{ padding: "10px 14px" }}>
                        <div className="font-medium" style={{ marginBottom: 4 }}>
                          <button className="table-sort" onClick={() => setActivePlayerModalId(entry.playerId)}>{entry.playerName}</button> ({entry.teamName})
                        </div>
                        <div className="text-sm text-gray-600">{entry.reason}</div>
                      </li>
                    ))}
                  </ul>
                </div>
              </>
            )}

            {panelId === "ruleViolations" && (
              <>
                <div className="flex justify-between items-center mb-2">
                  <h2 className="text-lg font-semibold">Rule Violations (Last 2 Weeks)</h2>
                  <Link to="/admin/rules" className="text-sm">Manage Rules</Link>
                </div>
                <div style={{ maxHeight: 190, overflowY: "auto", paddingRight: 4 }}>
                  <ul className="space-y-2" style={{ listStyle: "none", margin: 0, padding: 0 }}>
                    {violations.length === 0 && <li className="text-gray-600">No violations currently detected.</li>}
                    {violations.map((entry) => (
                      <li key={entry.id} className="border rounded p-3" style={{ padding: "10px 14px" }}>
                        <div className="font-medium" style={{ marginBottom: 4 }}>
                          <button className="table-sort" onClick={() => setActivePlayerModalId(entry.playerId)}>{entry.playerName}</button> ({entry.teamName})
                        </div>
                        <div className="space-y-1 mt-1">
                          {entry.violations.map((violation) => (
                            <div key={violation.id} className="text-sm text-gray-600">
                              <span className="font-medium">{violation.ruleName}</span> [{violation.source}] - {violation.message}
                            </div>
                          ))}
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              </>
            )}
          </div>
        ))}
      </div>
    </div>
    {activePlayerModalId && (
      <PlayerProfileModal
        playerId={activePlayerModalId}
        userRoles={userRoles}
        onClose={() => setActivePlayerModalId(null)}
      />
    )}
    </>
  );
};

export default AdminDashboard;
